import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';

import { FvElevatedComponent } from '@fv/ui-kit/components/elevated/elevated.component';
import { FvLogoComponent } from '@fv/ui-kit/components/logo/logo.component';
import { fvFormatClock, fvFormatRelative } from '@fv/ui-kit/utils/format-time';

import { StatusBannerComponent } from '../../components/status-banner/status-banner.component';
import { StatusCardComponent } from '../../components/status-card/status-card.component';
import { StatusReportComponent } from '../../components/status-report/status-report.component';
import { STATUS_PAGE_LOCALE } from '../../constants/status-page.constants';
import { SYSTEM_HEALTH_COPY } from '../../constants/system-health.copy';
import { SystemStatusPageQaKeys } from '../../qa-keys';
import { StatusPageConfigService } from '../../services/status-page-config.service';
import { SystemHealthStore } from '../../store/system-health.store';
import { interpolate } from '../../utils/interpolate';

/**
 * The page leads with problems: dependencies with an issue are listed, everything
 * available collapses into a single summary sentence instead of a row each.
 *
 * The meta row is deliberately not an aria-live region — the per-second countdown
 * would flood the polite queue; state changes are announced by the banner.
 */
@Component({
  selector: 'fv-system-status-page',
  standalone: true,
  imports: [FvElevatedComponent, FvLogoComponent, StatusBannerComponent, StatusCardComponent, StatusReportComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './system-status-page.component.html',
  styleUrl: './system-status-page.component.scss',
})
export class SystemStatusPageComponent {
  private readonly configService = inject(StatusPageConfigService);

  readonly qaKeys = SystemStatusPageQaKeys;
  readonly store = inject(SystemHealthStore);
  readonly copy = SYSTEM_HEALTH_COPY;
  readonly config = this.configService.config;

  readonly updatedLabel = computed(() => fvFormatClock(this.store.lastFetchedAt(), STATUS_PAGE_LOCALE));
  readonly dataAgeLabel = computed(() =>
    fvFormatRelative(this.store.generatedAt(), STATUS_PAGE_LOCALE, this.store.now())
  );
  readonly summaryLabel = computed(() => {
    const hasIssues = this.store.issues().length > 0;
    const count = hasIssues ? this.store.availableCount() : this.store.totalCount();
    const isSingular = count === 1;
    const template = hasIssues
      ? isSingular
        ? this.copy.summary.othersAvailableOne
        : this.copy.summary.othersAvailableMany
      : isSingular
        ? this.copy.summary.allAvailableOne
        : this.copy.summary.allAvailableMany;
    return interpolate(template, { count });
  });
  /** "All other 0 monitored services are available." during a total outage is worse than silence. */
  readonly isSummaryVisible = computed(
    () => this.store.hasLoaded() && this.store.totalCount() > 0 && this.store.availableCount() > 0
  );
  readonly footerLabel = computed(() =>
    interpolate(this.copy.footer, {
      year: new Date(this.store.now()).getFullYear(),
      company: this.config().company,
    })
  );
}
